/**
 * ComplianceResultsView - Privacy Compliance Results Page
 * ============================================================================
 * DC-821: Full Privacy Compliance findings view
 *
 * Displays:
 * - Journey header with compliance status
 * - Full findings panel with filtering by regulation and severity
 * - Regulation breakdown (GDPR, HIPAA, CCPA)
 * - Navigation back to Strategic dashboard
 *
 * @version 1.0.0
 * @created 2025-12-29
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import '../../styles/journey.css';

// ============================================================================
// Types
// ============================================================================

interface JourneyInfo {
  group_id: string;
  name: string;
  description?: string;
}

interface ComplianceFinding {
  id: string;
  regulation: string;
  category: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  evidence: string;
  remediation: string;
  column_name?: string;
}

interface ComplianceResult {
  overall_score: number;
  compliance_level: string;
  gdpr_score?: number;
  hipaa_score?: number;
  ccpa_score?: number;
  gdpr_applicable: boolean;
  hipaa_applicable: boolean;
  ccpa_applicable: boolean;
  findings: ComplianceFinding[];
  assessed_at: string;
}

// ============================================================================
// Constants
// ============================================================================

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#65a30d',
};

const REGULATION_INFO: Record<string, { name: string; fullName: string; color: string }> = {
  gdpr: { name: 'GDPR', fullName: 'EU General Data Protection Regulation', color: '#3b82f6' },
  hipaa: { name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act', color: '#8b5cf6' },
  ccpa: { name: 'CCPA', fullName: 'California Consumer Privacy Act', color: '#06b6d4' },
};

// ============================================================================
// Main Component
// ============================================================================

export const ComplianceResultsView: React.FC = () => {
  const { group_id } = useParams<{ group_id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useApiClient();

  const [journey, setJourney] = useState<JourneyInfo | null>(null);
  const [result, setResult] = useState<ComplianceResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<string[]>([]);
  const [regulationFilter, setRegulationFilter] = useState<string[]>([]);

  // Get initial filter from URL params
  const assessmentId = searchParams.get('assessment');
  const initialSeverity = searchParams.get('severity');

  // Initialize severity filter from URL
  useEffect(() => {
    if (initialSeverity) {
      setSeverityFilter(initialSeverity.split(','));
    }
  }, [initialSeverity]);

  // Fetch journey and compliance data
  useEffect(() => {
    if (group_id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch when IDs change
  }, [group_id, assessmentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch journey info
      const journeyResponse = await authFetch(`/api/journey/${group_id}`);

      if (!journeyResponse.ok) {
        throw new Error(`Failed to fetch journey: ${journeyResponse.statusText}`);
      }

      const journeyData = await journeyResponse.json();
      setJourney(journeyData.journey || journeyData);

      // FIX (2025-12-29): Correct endpoint path - no /latest suffix
      // GET /api/v1/lenses/privacy-compliance/:journeyId returns the latest assessment
      const complianceUrl = assessmentId
        ? `/api/v1/lenses/privacy-compliance/assessments/${assessmentId}`
        : `/api/v1/lenses/privacy-compliance/${group_id}`;

      const complianceResponse = await authFetch(complianceUrl);

      if (complianceResponse.status === 404) {
        setResult(null);
        setLoading(false);
        return;
      }

      if (!complianceResponse.ok) {
        throw new Error(`Failed to fetch compliance data: ${complianceResponse.statusText}`);
      }

      const complianceData = await complianceResponse.json();
      setResult(complianceData.result || complianceData);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[ComplianceResultsView] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  };

  // Filter findings
  const filteredFindings = result?.findings?.filter((finding) => {
    if (severityFilter.length > 0 && !severityFilter.includes(finding.severity)) {
      return false;
    }
    if (regulationFilter.length > 0 && !regulationFilter.includes(finding.regulation.toLowerCase())) {
      return false;
    }
    return true;
  }).sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]) || [];

  // Count findings by severity
  const findingCounts = {
    critical: result?.findings?.filter(f => f.severity === 'critical').length || 0,
    high: result?.findings?.filter(f => f.severity === 'high').length || 0,
    medium: result?.findings?.filter(f => f.severity === 'medium').length || 0,
    low: result?.findings?.filter(f => f.severity === 'low').length || 0,
  };

  const toggleSeverityFilter = (severity: string) => {
    setSeverityFilter(prev =>
      prev.includes(severity)
        ? prev.filter(s => s !== severity)
        : [...prev, severity]
    );
  };

  const toggleRegulationFilter = (regulation: string) => {
    setRegulationFilter(prev =>
      prev.includes(regulation)
        ? prev.filter(r => r !== regulation)
        : [...prev, regulation]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="compliance-results-view">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading compliance assessment...</p>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  // Error state
  if (error || !journey) {
    return (
      <div className="compliance-results-view">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load Assessment</h3>
          <p>{error || 'Journey not found'}</p>
          <button className="btn btn-secondary" onClick={() => navigate('/journey')}>
            ← Back to Dashboard
          </button>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  // No assessment state
  if (!result) {
    return (
      <div className="compliance-results-view">
        <div className="alcoa-results-header">
          <div className="header-breadcrumb">
            <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
              Journeys
            </button>
            <span className="breadcrumb-separator">/</span>
            <button className="breadcrumb-link" onClick={() => navigate(`/journey/${group_id}`)}>
              {journey.name}
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Privacy Compliance</span>
          </div>
        </div>
        <div className="empty-state">
          <div className="empty-icon">🔒</div>
          <h3>No Compliance Assessment Available</h3>
          <p>Run a Privacy Compliance assessment from the Strategic Dashboard to see detailed results here.</p>
          <button className="btn btn-secondary" onClick={() => navigate(`/journey/${group_id}`)}>
            ← Back to Strategic View
          </button>
        </div>
        <style>{viewStyles}</style>
      </div>
    );
  }

  return (
    <div className="compliance-results-view">
      {/* Header */}
      <div className="alcoa-results-header">
        <div className="header-breadcrumb">
          <button className="breadcrumb-link" onClick={() => navigate('/journey')}>
            Journeys
          </button>
          <span className="breadcrumb-separator">/</span>
          <button className="breadcrumb-link" onClick={() => navigate(`/journey/${group_id}`)}>
            {journey.name}
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Privacy Compliance</span>
        </div>

        <div className="header-title-row">
          <div className="header-left">
            <h1>🔒 Privacy Compliance Assessment</h1>
            <p className="subtitle">GDPR / HIPAA / CCPA Compliance Analysis</p>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="compliance-results-content">
        {/* Summary Sidebar */}
        <aside className="compliance-sidebar">
          {/* Overall Score Card */}
          <div className="summary-card">
            <h3>Overall Compliance</h3>
            <div className="score-display">
              <div className="score-value" style={{ color: result.overall_score >= 70 ? '#22c55e' : '#ef4444' }}>
                {Math.round(result.overall_score)}
              </div>
              <div className="score-label">/100</div>
            </div>
            <div className="compliance-level" style={{
              background: result.compliance_level === 'compliant' ? '#22c55e' :
                         result.compliance_level === 'partial' ? '#f59e0b' : '#ef4444'
            }}>
              {result.compliance_level?.toUpperCase() || 'UNKNOWN'}
            </div>
          </div>

          {/* Regulation Breakdown */}
          <div className="regulations-card">
            <h3>Regulation Scores</h3>
            {['gdpr', 'hipaa', 'ccpa'].map(reg => {
              const applicable = result[`${reg}_applicable` as keyof ComplianceResult];
              const score = result[`${reg}_score` as keyof ComplianceResult] as number;
              const info = REGULATION_INFO[reg];

              return (
                <div key={reg} className="regulation-row">
                  <div className="reg-header">
                    <span className="reg-name" style={{ color: info.color }}>{info.name}</span>
                    <span className="reg-score">
                      {applicable ? `${Math.round(score || 0)}%` : 'N/A'}
                    </span>
                  </div>
                  {applicable && (
                    <div className="reg-bar">
                      <div
                        className="reg-bar-fill"
                        style={{ width: `${score || 0}%`, background: info.color }}
                      />
                    </div>
                  )}
                  {!applicable && (
                    <div className="reg-not-applicable">not applicable</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Findings Summary */}
          <div className="findings-summary-card">
            <h3>Findings Summary</h3>
            <div className="findings-counts">
              {Object.entries(findingCounts).map(([severity, count]) => (
                <div
                  key={severity}
                  className={`finding-count ${severityFilter.includes(severity) ? 'active' : ''}`}
                  onClick={() => toggleSeverityFilter(severity)}
                  style={{ '--severity-color': SEVERITY_COLORS[severity] } as React.CSSProperties}
                >
                  <span className="count-value">{count}</span>
                  <span className="count-label">{severity}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Findings Panel */}
        <main className="compliance-main">
          {/* Filters */}
          <div className="filters-bar">
            <div className="filter-group">
              <span className="filter-label">Regulation:</span>
              {Object.entries(REGULATION_INFO).map(([key, info]) => (
                <button
                  key={key}
                  className={`filter-chip ${regulationFilter.includes(key) ? 'active' : ''}`}
                  onClick={() => toggleRegulationFilter(key)}
                  style={{ '--chip-color': info.color } as React.CSSProperties}
                >
                  {info.name}
                </button>
              ))}
            </div>
            <div className="filter-group">
              <span className="filter-label">Severity:</span>
              {Object.keys(SEVERITY_COLORS).map(severity => (
                <button
                  key={severity}
                  className={`filter-chip ${severityFilter.includes(severity) ? 'active' : ''}`}
                  onClick={() => toggleSeverityFilter(severity)}
                  style={{ '--chip-color': SEVERITY_COLORS[severity] } as React.CSSProperties}
                >
                  {severity}
                </button>
              ))}
            </div>
            {(severityFilter.length > 0 || regulationFilter.length > 0) && (
              <button
                className="clear-filters"
                onClick={() => { setSeverityFilter([]); setRegulationFilter([]); }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Findings List */}
          <div className="findings-list">
            {filteredFindings.length === 0 ? (
              <div className="no-findings">
                <p>✓ No findings match the current filters</p>
                {result.findings?.length === 0 && (
                  <p className="hint">This assessment found no compliance issues - great job!</p>
                )}
              </div>
            ) : (
              filteredFindings.map(finding => (
                <div key={finding.id} className="finding-card">
                  <div className="finding-header">
                    <span
                      className="finding-severity"
                      style={{ background: SEVERITY_COLORS[finding.severity] }}
                    >
                      {finding.severity.toUpperCase()}
                    </span>
                    <span
                      className="finding-regulation"
                      style={{ color: REGULATION_INFO[finding.regulation.toLowerCase()]?.color }}
                    >
                      {finding.regulation}
                    </span>
                    <span className="finding-category">{finding.category}</span>
                  </div>
                  <p className="finding-description">{finding.description}</p>
                  {finding.column_name && (
                    <div className="finding-column">
                      <strong>Affected Column:</strong> {finding.column_name}
                    </div>
                  )}
                  {finding.evidence && (
                    <div className="finding-evidence">
                      <strong>Evidence:</strong> {finding.evidence}
                    </div>
                  )}
                  <div className="finding-remediation">
                    <strong>Recommended Action:</strong> {finding.remediation}
                  </div>
                </div>
              ))
            )}
          </div>
        </main>
      </div>

      {/* Footer */}
      <div className="alcoa-results-footer">
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/journey/${group_id}`)}
        >
          ← Back to Strategic View
        </button>
      </div>

      <style>{viewStyles}</style>
    </div>
  );
};

// ============================================================================
// Styles
// ============================================================================

const viewStyles = `
  .compliance-results-view {
    min-height: 100vh;
    background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
    padding: 1.5rem;
    color: #e2e8f0;
  }

  .alcoa-results-header {
    margin-bottom: 1.5rem;
  }

  .header-breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #94a3b8;
    margin-bottom: 1rem;
  }

  .breadcrumb-link {
    background: none;
    border: none;
    color: #60a5fa;
    cursor: pointer;
    padding: 0;
  }

  .breadcrumb-link:hover {
    text-decoration: underline;
  }

  .breadcrumb-separator {
    color: #475569;
  }

  .breadcrumb-current {
    color: #e2e8f0;
  }

  .header-title-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .header-left h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #f1f5f9;
    margin: 0 0 0.25rem;
  }

  .header-left .subtitle {
    font-size: 0.95rem;
    color: #94a3b8;
    margin: 0;
  }

  .compliance-results-content {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 1024px) {
    .compliance-results-content {
      grid-template-columns: 1fr;
    }
  }

  .compliance-sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .summary-card, .regulations-card, .findings-summary-card {
    background: rgba(30, 41, 59, 0.8);
    border: 1px solid #334155;
    border-radius: 12px;
    padding: 1.25rem;
  }

  .summary-card h3, .regulations-card h3, .findings-summary-card h3 {
    font-size: 0.875rem;
    font-weight: 600;
    color: #94a3b8;
    margin: 0 0 1rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .score-display {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
    margin-bottom: 1rem;
  }

  .score-value {
    font-size: 3rem;
    font-weight: 700;
  }

  .score-label {
    font-size: 1.25rem;
    color: #64748b;
  }

  .compliance-level {
    text-align: center;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 600;
    color: white;
  }

  .regulation-row {
    margin-bottom: 1rem;
  }

  .regulation-row:last-child {
    margin-bottom: 0;
  }

  .reg-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .reg-name {
    font-weight: 600;
  }

  .reg-score {
    font-weight: 500;
    color: #94a3b8;
  }

  .reg-bar {
    height: 6px;
    background: #1e293b;
    border-radius: 3px;
    overflow: hidden;
  }

  .reg-bar-fill {
    height: 100%;
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  .reg-not-applicable {
    font-size: 0.75rem;
    color: #64748b;
    font-style: italic;
  }

  .findings-counts {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  .finding-count {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid #334155;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .finding-count:hover, .finding-count.active {
    border-color: var(--severity-color);
    background: rgba(15, 23, 42, 0.9);
  }

  .finding-count.active {
    box-shadow: 0 0 0 2px var(--severity-color);
  }

  .count-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--severity-color);
  }

  .count-label {
    font-size: 0.75rem;
    color: #94a3b8;
    text-transform: capitalize;
  }

  .compliance-main {
    min-width: 0;
  }

  .filters-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background: rgba(30, 41, 59, 0.6);
    border-radius: 8px;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .filter-label {
    font-size: 0.875rem;
    color: #94a3b8;
  }

  .filter-chip {
    padding: 0.375rem 0.75rem;
    background: transparent;
    border: 1px solid #475569;
    border-radius: 16px;
    color: #94a3b8;
    font-size: 0.8125rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .filter-chip:hover {
    border-color: var(--chip-color);
    color: var(--chip-color);
  }

  .filter-chip.active {
    background: var(--chip-color);
    border-color: var(--chip-color);
    color: white;
  }

  .clear-filters {
    margin-left: auto;
    padding: 0.375rem 0.75rem;
    background: transparent;
    border: none;
    color: #60a5fa;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .clear-filters:hover {
    text-decoration: underline;
  }

  .findings-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .finding-card {
    background: rgba(30, 41, 59, 0.8);
    border: 1px solid #334155;
    border-radius: 12px;
    padding: 1.25rem;
  }

  .finding-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .finding-severity {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.6875rem;
    font-weight: 600;
    color: white;
  }

  .finding-regulation {
    font-weight: 600;
    font-size: 0.875rem;
  }

  .finding-category {
    font-size: 0.8125rem;
    color: #94a3b8;
  }

  .finding-description {
    font-size: 0.9375rem;
    color: #e2e8f0;
    margin: 0 0 0.75rem;
    line-height: 1.5;
  }

  .finding-column, .finding-evidence, .finding-remediation {
    font-size: 0.8125rem;
    color: #94a3b8;
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    background: rgba(15, 23, 42, 0.5);
    border-radius: 6px;
  }

  .finding-column strong, .finding-evidence strong, .finding-remediation strong {
    color: #cbd5e1;
  }

  .finding-remediation {
    border-left: 3px solid #22c55e;
    margin-bottom: 0;
  }

  .no-findings {
    text-align: center;
    padding: 3rem;
    color: #64748b;
  }

  .no-findings .hint {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #22c55e;
  }

  .alcoa-results-footer {
    padding-top: 1rem;
    border-top: 1px solid #334155;
  }

  /* Button styles */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary {
    background: rgba(139, 92, 246, 0.1);
    color: #c4b5fd;
    border: 1px solid rgba(139, 92, 246, 0.3);
  }

  .btn-secondary:hover {
    background: rgba(139, 92, 246, 0.2);
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
  }

  .error-icon, .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #334155;
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default ComplianceResultsView;
